import { mapActions, mapGetters, mapState } from 'vuex';
import researchWidget from '@/components/research-widget/research-widget.vue';
import twitterTimeline from '@/components/twitter/twitter-timeline/twitter-timeline.vue';
import insightWidget from '@/components/insight/insight-widget/insight-widget.vue';
import marketBiggestGains from '@/components/market/market-biggest-gains/market-biggest-gains.vue';
import marketMoneyWidget from '@/components/market/market-money-widget/market-money-widget.vue';
import marketCurrentlyPredicting from '@/components/market/market-currently-predicting/market-currently-predicting.vue';
import marketKeyStats from '@/components/market/market-key-stats/market-key-stats.vue';
import marketTopComments from '@/components/market/market-top-comments/market-top-comments.vue';
import insightNews from '@/components/insight/insight-news/insight-news.vue';
import previewCarousel from '@/components/cms/preview-carousel/preview-carousel.vue';
import { MODALS } from '@/components/app/app-modals/modals-constants';
import contentLoaderMixin from '@/mixins/contentLoader';
import loaderMixin from '@/mixins/loader';
import seoMetaMixin from '@/mixins/seoMeta';
import electionMap from '@/components/pages/page-election-map-new/election-map/election-map.vue';
import dailyMarkets from '@/components/daily-markets/daily-markets.vue';
import pageMarkets from '@/components/pages/page-markets/page-markets.vue';
import buildCanonicalUrl from '@/utils/canonical-url';
import { buildJsonLdTag, jsonLdSanitizerOverride, buildOrganizationWebsiteSchema } from '@/utils/json-ld';
import newsFeed from './news-feed/news-feed.vue';
import podcasts from './podcasts/podcasts.vue';
import heroUnitSlider from './hero-unit-slider/hero-unit-slider.vue';
import quotes from './quotes/quotes.vue';
import howItWorks from './how-it-works/how-it-works.vue';
import mostPredicted from './most-predicted/most-predicted.vue';
import mostPredictedList from './most-predicted-list/most-predicted-list.vue';
import homeVideos from './home-videos';
import { lazyWithReload } from '@/helpers/reloadOnChunkError';

const HOME_JSON_LD_VMID = 'jsonld-home';

export default {
  mixins: [
    contentLoaderMixin,
    loaderMixin,
    seoMetaMixin,
  ],
  components: {
    electionMap,
    heroUnitSlider,
    quotes,
    howItWorks,
    mostPredicted,
    podcasts,
    researchWidget,
    inTheNews: lazyWithReload(() => import(/* webpackChunkName: "home-in-the-news" */ './in-the-news/in-the-news.vue')),
    twitterTimeline,
    newsFeed,
    mostPredictedList,
    insightWidget,
    marketBiggestGains,
    marketMoneyWidget,
    marketCurrentlyPredicting,
    marketKeyStats,
    marketTopComments,
    homeVideos,
    insightNews,
    dailyMarkets,
    pageMarkets,
    previewCarousel,
  },
  metaInfo() {
    const canonicalUrl = buildCanonicalUrl(this.$router, 'home');
    const homeTitle = 'PredictIt: The Original Political Prediction Market';
    const homeDescription = 'Trade political prediction markets on PredictIt. Follow election odds, track political events and see how real people price what happens next.';
    const jsonLdSchema = buildOrganizationWebsiteSchema({
      origin: window.location.origin,
      homeName: homeTitle,
      websiteDescription: homeDescription,
      sameAs: [
        process.env.PI_FACEBOOK_LINK,
        process.env.PI_INSTAGRAM_LINK,
        process.env.PI_TWITTER_LINK,
        process.env.PI_YOUTUBE_LINK,
      ],
    });
    return {
      ...this.buildSeoMeta({
        title: homeTitle,
        description: homeDescription,
        url: canonicalUrl,
      }),
      script: [buildJsonLdTag(jsonLdSchema, HOME_JSON_LD_VMID)],
      __dangerouslyDisableSanitizersByTagID: jsonLdSanitizerOverride(HOME_JSON_LD_VMID),
    };
  },
  computed: {
    ...mapGetters('app', ['isLayoutDesktop', 'isLayoutMobile', 'isShutdownModeOn']),
    ...mapGetters('user', ['isUserRestricted', 'isLoggedIn']),
    ...mapState('popup', ['currentPopup']),
    classModifiers() {
      return {
        'home-old': true,
        'home-old--shutdown': this.isShutdownModeOn,
      };
    },
    showMap() {
      return process.env.SHOW_MAP_ON_HOMEPAGE;
    },
  },
  methods: {
    ...mapActions('popup', ['setCurrentPopup']),
    showHomepageInitialLoader() {
      if (this.homepageInitialLoaderVisible) {
        return;
      }
      this.loaderAdd(document.body);
      this.homepageInitialLoaderVisible = true;
    },
    hideHomepageInitialLoader() {
      if (!this.homepageInitialLoaderVisible) {
        return;
      }
      this.loaderRemove(document.body);
      this.homepageInitialLoaderVisible = false;
    },
    onEmbeddedInitialLoadStateChange(isLoading) {
      if (this.initialHomepageMarketLoadComplete) {
        return;
      }
      if (isLoading) {
        this.showHomepageInitialLoader();
      } else {
        this.hideHomepageInitialLoader();
        this.initialHomepageMarketLoadComplete = true;
      }
    },
  },
  data() {
    return {
      initialHomepageMarketLoadComplete: false,
      homepageInitialLoaderVisible: false,
    };
  },
  mounted() {
    if (!this.initialHomepageMarketLoadComplete) {
      this.showHomepageInitialLoader();
    }
    if (this.isUserRestricted && !this.currentPopup) {
      this.setCurrentPopup(MODALS.LOGIN);
    }
  },
  beforeDestroy() {
    this.hideHomepageInitialLoader();
  },
};



// WEBPACK FOOTER //
// ./src/components/home/home-old/home-old.js